import { useMemo } from 'react';
import { ArrowLeftRight, Smartphone } from 'lucide-react';
import { useParqueMovelByCnpj, useCoberturaClaroByCep } from '@/hooks/useParqueVivoByCnpj';
import { usePlanosCpfEscada } from '@/hooks/usePlanosCpfEscada';
import { usePlanosCpfOferta } from '@/hooks/usePlanosCpfOferta';
import type { LeadCarteira } from '@/hooks/useLeadsCarteira';
import {
  Bloco, BlocoComoFalar, BlocoOferta, BlocoOndeMora, BlocoPorQueLigar,
  Par, Tile, Tiles, cnpjFmt, moeda, telefoneFmt, type Oferta,
} from './lead-cpf-blocos';

const SITUACAO_TEXTO: Record<string, string> = {
  BAIXADA: 'baixada',
  INAPTA: 'inapta',
  SUSPENSA: 'suspensa',
};

interface Props {
  lead: LeadCarteira;
  onLigar?: (telefone: string) => void;
  onLevarParaProposta?: (ofertas: Oferta[]) => void;
}

// Portabilidade CPF: a empresa morreu na Receita, as linhas seguem vivas na Vivo
// e podem vir para o CPF do titular. Mesmo esqueleto da Renovação CPF — o que
// muda é o conteúdo de cada bloco.
export function LeadDados360PortabilidadeCpf({ lead, onLigar, onLevarParaProposta }: Props) {
  const master = (lead.dados_master_lead || {}) as Record<string, unknown>;
  const cnpjOrigem = (master.cnpj_origem as string) || null;
  // A razão social de MEI termina com o CPF ("FULANO 06510604272"): na tela o
  // documento não entra no nome — ele já aparece no cabeçalho do lead.
  const razaoOrigem = ((master.razao_social_origem as string) || '').replace(/\s*\d{11}\s*$/, '').trim() || null;
  const situacao = ((master.situacao_receita as string) || '').toUpperCase();

  // BASE VIVO PF: a pessoa é cliente Vivo no próprio CPF, sem empresa no meio.
  // As linhas vêm da base da Vivo gravada no lead, não do parque de um CNPJ.
  const vivoPf = master.origem_cpf === 'BASE_VIVO_PF';
  const { data: parqueCnpj = [], isLoading } = useParqueMovelByCnpj(vivoPf ? null : cnpjOrigem);
  const linhasVivoPf = useMemo(() => {
    const dm = (lead.dados_master_lead || {}) as Record<string, unknown>;
    if (dm.origem_cpf !== 'BASE_VIVO_PF') return [];
    const base = (dm.base_linhas_movel as { telefone?: string; fatura_media?: number | null }[] | undefined) || [];
    return base.map((l) => ({
      id: String(l.telefone ?? ''),
      nr_telefone: l.telefone ?? null,
      plano: null,
      fat_medio_03_meses: l.fatura_media ?? null,
      m: null,
      qtd_gb_contratado_dados: null,
      qtd_gb_traf_dados: null,
      aparelho_trafego_modelo: null,
      nr_cep: null,
      ds_municipio: null,
      tem_cobertura_banda_larga: null,
    })) as unknown as typeof parqueCnpj;
  }, [lead.dados_master_lead]);
  const parque = vivoPf ? linhasVivoPf : parqueCnpj;
  const clienteDesde = (master.cliente_vivo_desde as string) || null;
  const { data: escada = [] } = usePlanosCpfEscada();
  const { data: catalogo } = usePlanosCpfOferta();

  const cep = vivoPf ? ((master.cep as string) || null) : parque.find((l) => l.nr_cep)?.nr_cep || null;
  const cidade = vivoPf ? ((master.cidade as string) || null) : parque.find((l) => l.ds_municipio)?.ds_municipio || null;
  const { data: coberturaClaro } = useCoberturaClaroByCep(cep);
  const temCobertura = coberturaClaro?.coberto === true;
  const coberturaBase = vivoPf
    ? lead.cobertura_fibra === 'SIM'
    : parque.some((l) => String(l.tem_cobertura_banda_larga || '').toUpperCase().startsWith('S'));

  const totais = useMemo(() => {
    const somaLinhas = parque.reduce((s, l) => s + (Number(l.fat_medio_03_meses) || 0), 0);
    const valor = vivoPf ? Number(master.valor_vivo) || somaLinhas : somaLinhas;
    const gbContratado = parque.reduce((s, l) => s + (Number(l.qtd_gb_contratado_dados) || 0), 0);
    const gbUsado = parque.reduce((s, l) => s + (Number(l.qtd_gb_traf_dados) || 0), 0);
    return {
      linhas: parque.length,
      valor,
      gbContratado,
      gbUsado,
      usoPct: gbContratado > 0 ? Math.round((gbUsado / gbContratado) * 100) : 0,
    };
  }, [parque, vivoPf, master.valor_vivo]);

  // O plano sugerido sai do GB que ele USA, nunca do que contratou: é onde está
  // a margem. Cobre o uso com folga, do mais barato para cima.
  const comparativo = useMemo(
    () => parque.map((linha) => {
      const usado = Number(linha.qtd_gb_traf_dados) || 0;
      const sugerido = escada.find((p) => p.gb >= Math.max(usado * 1.5, 10)) || escada[escada.length - 1];
      return { linha, sugerido };
    }),
    [parque, escada],
  );

  const totalNovo = comparativo.reduce((s, c) => s + (c.sugerido?.valor ?? 0), 0);
  const gbNovo = comparativo.reduce((s, c) => s + (c.sugerido?.gb ?? 0), 0);
  const economia = totais.valor - totalNovo;
  const temSugestao = comparativo.some((c) => c.sugerido);

  // A oferta: portar é o item principal; fibra e TV só entram com cobertura.
  const ofertas = useMemo<Oferta[]>(() => {
    const lista: Oferta[] = [];
    if (totais.linhas > 0 && temSugestao) {
      lista.push({
        nome: `Portar ${totais.linhas} linha${totais.linhas > 1 ? 's' : ''} para o CPF`,
        valor: totalNovo,
        destaque: true,
        selo: 'O motivo da ligação',
        porque: `Hoje paga ${moeda(totais.valor)} na Vivo. No CPF ele fica com as mesmas linhas e ${gbNovo} GB.`,
      });
    }
    if (temCobertura && catalogo?.fibra) {
      lista.push({
        nome: catalogo.fibra.nome, valor: catalogo.fibra.valor,
        selo: 'Tem cobertura no CEP',
        porque: 'O endereço das linhas é atendido por fibra. Entra junto com a portabilidade.',
      });
    }
    if (temCobertura && catalogo?.tv) {
      lista.push({
        nome: catalogo.tv.nome, valor: catalogo.tv.valor,
        selo: 'Depende da fibra',
        porque: 'Fecha com a fibra e sustenta o ticket.',
      });
    }
    if (totais.gbContratado > 0 && totais.usoPct < 60 && catalogo?.dependente) {
      lista.push({
        nome: catalogo.dependente.nome, valor: catalogo.dependente.valor,
        selo: 'Sem pacote próprio',
        porque: `Divide o pacote do titular — ele usa ${totais.usoPct}% do que contrata.`,
      });
    }
    return lista;
  }, [totais, temSugestao, totalNovo, gbNovo, temCobertura, catalogo]);

  const telefones = [lead.telefone, lead.telefone_2, lead.telefone_3, lead.telefone_4, lead.telefone_adm]
    .map((t) => String(t ?? '').replace(/\D/g, ''))
    .filter((t, i, arr) => t.length >= 10 && arr.indexOf(t) === i);

  return (
    <div className="space-y-3">
      {vivoPf ? (
      <BlocoPorQueLigar
        meta="cliente Vivo no CPF"
        frase={
          <>
            Ele é <b>cliente Vivo no CPF</b>
            {clienteDesde ? <> desde <b>{clienteDesde}</b></> : null}
            {totais.linhas > 0 ? (
              <>, com <b>{totais.linhas} linha{totais.linhas > 1 ? 's' : ''}</b> por <b>{moeda(totais.valor)}</b> por mês</>
            ) : null}
            .{' '}
            {coberturaBase ? <>Tem <b>fibra da Claro na rua dele</b>: leva o Multi com as linhas e a internet de casa.</> : <>Confirme o endereço antes de falar de fibra.</>}
          </>
        }
      >
        <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3">
          <Par rotulo="Origem" valor="Base Vivo PF" />
          <Par rotulo="Cliente Vivo desde" valor={clienteDesde || '—'} />
          <Par rotulo="Operadora hoje" valor={lead.operadora_atual || 'Vivo'} />
          <Par rotulo="Fibra na rua" valor={coberturaBase ? 'Sim' : 'Não'} />
          <Par rotulo="Tecnologia" valor={(master.cobertura_detalhe as string) || '—'} />
        </div>
      </BlocoPorQueLigar>
      ) : (
      <BlocoPorQueLigar
        meta="portabilidade para o CPF"
        frase={
          <>
            A empresa dele está <b>{SITUACAO_TEXTO[situacao] || 'morta'} na Receita</b>
            {totais.linhas > 0 ? (
              <>
                , mas as <b>{totais.linhas} linha{totais.linhas > 1 ? 's' : ''} seguem ativas na Vivo</b> por{' '}
                <b>{moeda(totais.valor)}</b> por mês
                {totais.gbContratado > 0 ? <> usando <b>{totais.gbUsado} GB de {totais.gbContratado}</b></> : null}
                . As linhas podem vir para o CPF dele.
              </>
            ) : (
              <>. Confirme na ligação quais linhas ele ainda tem.</>
            )}
          </>
        }
      >
        <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3">
          <Par rotulo="Empresa de origem" valor={razaoOrigem || '—'} title={razaoOrigem || undefined} />
          <Par rotulo="CNPJ de origem" valor={cnpjFmt(cnpjOrigem)} />
          <Par rotulo="Situação na Receita" valor={situacao || '—'} />
          <Par rotulo="Operadora hoje" valor={lead.operadora_atual || 'Vivo'} />
          <Par rotulo="Tempo de contrato" valor={lead.m_atual ? `${lead.m_atual} meses` : '—'} />
        </div>
      </BlocoPorQueLigar>
      )}

      <BlocoComoFalar
        master={master}
        telefones={telefones}
        email={lead.email}
        onLigar={onLigar}
        semGestor={vivoPf ? 'Ele é o titular da conta Vivo: ligue direto para ele, nos números das linhas.' : undefined}
      />

      <BlocoOferta ofertas={ofertas} onLevarParaProposta={onLevarParaProposta} />

      <Bloco titulo="Base operadora (Vivo)" meta={vivoPf ? 'pelo CPF' : 'pelo CNPJ de origem'} icone={<Smartphone className="h-3 w-3" />}>
        {isLoading ? (
          <p className="text-[12px] text-muted-foreground">Carregando as linhas…</p>
        ) : parque.length === 0 ? (
          <p className="text-[12px] text-muted-foreground">
            {vivoPf ? 'A base da Vivo não trouxe as linhas deste CPF.' : 'A base da Vivo não tem linhas para este CNPJ.'} Confirme as linhas na ligação.
          </p>
        ) : (
          <>
            {vivoPf ? (
              <>
                <Tiles>
                  <Tile rotulo="Linhas" valor={String(totais.linhas)} />
                  <Tile rotulo="Conta Vivo /mês" valor={moeda(totais.valor)} />
                  <Tile rotulo="Cliente desde" valor={clienteDesde || '—'} />
                  <Tile rotulo="Fibra na rua" valor={coberturaBase ? 'Sim' : 'Não'} />
                </Tiles>
                <div className="flex flex-wrap gap-2">
                  {parque.map((l) => (
                    <span key={l.id} className="rounded-lg border border-border px-2.5 py-1.5 text-[12.5px] font-semibold tabular-nums">
                      {telefoneFmt(l.nr_telefone)}
                      {l.fat_medio_03_meses != null ? <span className="font-normal text-muted-foreground"> · {moeda(l.fat_medio_03_meses)}</span> : null}
                    </span>
                  ))}
                </div>
                <p className="text-[12px] text-muted-foreground">
                  A base da Vivo traz as linhas e o valor da conta. Plano, GB e fidelidade se confirmam na ligação.
                </p>
              </>
            ) : (
            <>
            <Tiles>
              <Tile rotulo="Linhas" valor={String(totais.linhas)} />
              <Tile rotulo="Fatura total /mês" valor={moeda(totais.valor)} />
              <Tile rotulo="Contratado" valor={`${totais.gbContratado} GB`} />
              <Tile rotulo="Uso real" valor={`${totais.gbUsado} GB`} sufixo={`· ${totais.usoPct}%`} />
            </Tiles>
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="border-b border-border text-left text-[8.5px] font-bold uppercase tracking-[1.1px] text-muted-foreground">
                    <th className="py-2 pr-2">Linha</th>
                    <th className="py-2 pr-2">Plano</th>
                    <th className="py-2 pr-2">Fatura</th>
                    <th className="py-2 pr-2">M</th>
                    <th className="py-2 pr-2">GB contr.</th>
                    <th className="py-2 pr-2">GB usado</th>
                    <th className="py-2">Aparelho</th>
                  </tr>
                </thead>
                <tbody>
                  {parque.map((l) => (
                    <tr key={l.id} className="h-11 border-b border-border last:border-0 odd:bg-[#FBF9F6] dark:odd:bg-[#241A1A]">
                      <td className="max-w-[140px] truncate pr-2 font-semibold">{telefoneFmt(l.nr_telefone)}</td>
                      <td className="max-w-[220px] truncate pr-2" title={l.plano || undefined}>{l.plano || '—'}</td>
                      <td className="pr-2 tabular-nums">{moeda(l.fat_medio_03_meses)}</td>
                      <td className="pr-2 tabular-nums">{l.m ?? '—'}</td>
                      <td className="pr-2 tabular-nums">{l.qtd_gb_contratado_dados ?? '—'}</td>
                      <td className="pr-2 tabular-nums">{l.qtd_gb_traf_dados ?? '—'}</td>
                      <td className="max-w-[180px] truncate" title={l.aparelho_trafego_modelo || undefined}>
                        {l.aparelho_trafego_modelo || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
            )}
          </>
        )}
      </Bloco>

      {parque.length > 0 && temSugestao && (!vivoPf || totais.gbContratado > 0) ? (
        <Bloco titulo="Como ficaria na Claro" meta="sugestão pelo GB que ele usa" icone={<ArrowLeftRight className="h-3 w-3" />}>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="border-b border-border text-left text-[8.5px] font-bold uppercase tracking-[1.1px] text-muted-foreground">
                  <th className="py-2 pr-2">Linha</th>
                  <th className="py-2 pr-2">Hoje na Vivo</th>
                  <th className="py-2 pr-2">GB hoje</th>
                  <th className="py-2 pr-2">Paga hoje</th>
                  <th className="py-2 pr-2">Na Claro</th>
                  <th className="py-2 pr-2">GB novo</th>
                  <th className="py-2">Passa a pagar</th>
                </tr>
              </thead>
              <tbody>
                {comparativo.map(({ linha, sugerido }) => (
                  <tr key={linha.id} className="h-11 border-b border-border last:border-0 odd:bg-[#FBF9F6] dark:odd:bg-[#241A1A]">
                    <td className="max-w-[140px] truncate pr-2 font-semibold">{telefoneFmt(linha.nr_telefone)}</td>
                    <td className="max-w-[200px] truncate pr-2" title={linha.plano || undefined}>{linha.plano || '—'}</td>
                    <td className="pr-2 tabular-nums">
                      {linha.qtd_gb_contratado_dados ?? '—'} · usa {linha.qtd_gb_traf_dados ?? 0}
                    </td>
                    <td className="pr-2 tabular-nums">{moeda(linha.fat_medio_03_meses)}</td>
                    <td className="max-w-[220px] truncate pr-2" title={sugerido?.nome}>{sugerido?.nome || '—'}</td>
                    <td className="pr-2 tabular-nums">{sugerido?.gb ?? '—'}</td>
                    <td className="tabular-nums">{moeda(sugerido?.valor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Tiles>
            <Tile rotulo="Total hoje na Vivo" valor={moeda(totais.valor)} />
            <Tile rotulo="Total novo na Claro" valor={moeda(totalNovo)} />
            <Tile rotulo={economia >= 0 ? 'Economia por mês' : 'Aumento por mês'} valor={moeda(Math.abs(economia))} />
            <Tile rotulo="Internet" valor={`${totais.gbContratado} → ${gbNovo} GB`} />
          </Tiles>
          <p className="text-[12px] text-muted-foreground">
            A sugestão cobre com folga o GB que ele usa de verdade e sai do catálogo CPF do CRM. O consultor troca o
            plano de qualquer linha na proposta.
          </p>
        </Bloco>
      ) : null}

      <BlocoOndeMora
        endereco={vivoPf ? ((master.endereco as string) || null) : cidade ? `${cidade} — ${lead.uf || ''}` : null}
        cidade={cidade}
        uf={lead.uf}
        cep={cep}
        coberturaBase={parque.length > 0 ? coberturaBase : null}
        coberturaNossa={coberturaClaro == null ? null : temCobertura}
        fonte={vivoPf ? 'endereço da conta Vivo' : 'endereço das linhas na Vivo'}
      />
    </div>
  );
}
